import { Button, NativeSelect, TextInput } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUpdateUserForm } from 'src/Hooks/Forms/useUpdateUserForm';
import { useUpdateUser } from 'src/Hooks/Mutations/useUpdateUser';
import useFetchUser from 'src/Hooks/Queries/useFetchUser';
import { UpdateUserDtoTitleEnum } from 'src/Services/API/generated';

import MantinePhoneNumberInput from '../MantinePhoneNumberInput/MantinePhoneNumberInput.component';

import styles from './CompleteUserProfileForm.module.scss';
import { Props } from './CompleteUserProfileForm.types';

const CompleteUserProfileForm = ({ onSuccess }: Props) => {
  const { t } = useTranslation();
  const form = useUpdateUserForm();
  const { data: user, isLoading } = useFetchUser();
  const [formReady, setFormReady] = useState<boolean>(false);

  const { mutate, isLoading: isUpdatingUser } = useUpdateUser({ onSuccess });

  useEffect(() => {
    if (user) {
      form.setValues({
        title: (user.title as UpdateUserDtoTitleEnum) ?? UpdateUserDtoTitleEnum.Mr,
        firstName: user.firstName,
        lastName: user.lastName,
        birthDate: user.birthDate?.toDate(),
      });

      setFormReady(true);
    }
  }, [user]);

  return (
    formReady && (
      <form onSubmit={form.onSubmit(values => mutate(values))} className={styles.form}>
        <div className={styles.subcontainer}>
          <NativeSelect
            data={Object.entries(UpdateUserDtoTitleEnum).map(([key]) => ({
              value: UpdateUserDtoTitleEnum[key as keyof typeof UpdateUserDtoTitleEnum],
              label: t(
                `registerForm.titles.${UpdateUserDtoTitleEnum[key as keyof typeof UpdateUserDtoTitleEnum]}`
              ),
            }))}
            description={t('updateUserForm.userTitle', 'Titre')}
            className={styles.titleSelect}
            {...form.getInputProps('title')}
          />
          <div className={styles.formLine}>
            <TextInput
              placeholder={t('updateUserForm.typeFirstName', 'Entrez votre prénom')}
              description={t('updateUserForm.firstNameDescription', 'Prénom')}
              {...form.getInputProps('firstName')}
            />
            <TextInput
              placeholder={t('updateUserForm.typeLastName', 'Entrez votre nom')}
              description={t('updateUserForm.lastNameDescription', 'Nom')}
              {...form.getInputProps('lastName')}
            />
          </div>
          <MantinePhoneNumberInput
            description={t(
              'registerForm.phoneDescription',
              'Numéro de téléphone - Important pour recevoir des notifications de votre famille'
            )}
            initialCountryCode={'FR'}
            {...form.getInputProps('phoneNumber')}
          />
          <DateInput
            description={t(
              'updateUserForm.birthDateDescription',
              "Date de naissance - Important pour les rappels d'anniversaire en famille"
            )}
            {...form.getInputProps('birthDate')}
            valueFormat="DD/MM/YYYY"
            defaultLevel="decade"
          />
          <Button
            variant="filled"
            type="submit"
            loading={isLoading || isUpdatingUser}
            disabled={isLoading}
          >
            {t('updateUserForm.register', 'Continuer')}
          </Button>
        </div>
      </form>
    )
  );
};

export default CompleteUserProfileForm;
